import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '~/components/Auth'
import { buyListing, TradeError, TradeListing, TradeOrder } from '../api/trade'
import { TradeDialog } from './TradeDialog'

/** What to tell the buyer for the codes they can actually fix.
 *
 *  Anything else falls through to the server's own `message`, which is already
 *  written for a player. */
const BUY_ERRORS: Record<string, string> = {
    price_changed:
        'The seller changed the price while you were looking. The listing has been refreshed, so check the new price and try again.',
    insufficient_balance: 'Your BR1 balance is too low for this purchase. Add funds and try again.',
    wallet_not_linked:
        'That wallet is not linked to your account. Pick one of your linked wallets, or link it first.',
}

const shortKey = (key: string) => `${key.slice(0, 4)}...${key.slice(-4)}`

/** Confirms a purchase before any money moves.
 *
 *  Buying needs no signature, so this deliberately does not touch the wallet
 *  adapter: the delivery address is chosen from the wallets already linked to
 *  the account, which is also what the server checks it against.
 *
 *  Mount it only while a listing is selected. The idempotency key is made when
 *  it mounts, so one opening is one key: a retry after a dropped connection or
 *  a failed attempt re-sends the same key (one debit at most), and opening the
 *  dialog again is a fresh intent with a fresh key. */
export const BuyDialog = ({
    listing,
    onClose,
    onBought,
    onPriceChanged,
}: {
    listing: TradeListing
    onClose: () => void
    onBought: (order: TradeOrder) => void
    onPriceChanged: () => void
}) => {
    const { auth } = useAuth()
    const wallets = auth?.wallets ?? []
    const idempotencyKey = useRef(crypto.randomUUID())
    const [wallet, setWallet] = useState(wallets[0]?.pubkey ?? '')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // After price_changed this dialog's price is wrong for good; retrying with it
    // can only fail again, so the only way forward is to close and reopen.
    const [stale, setStale] = useState(false)

    const confirm = async () => {
        setBusy(true)
        setError(null)
        try {
            const { order } = await buyListing({
                listingId: listing.id,
                wallet,
                // What the card displayed, so a reprice cannot slip in between.
                expectedPrice: listing.price,
                idempotencyKey: idempotencyKey.current,
            })
            onBought(order)
        } catch (e) {
            if (e instanceof TradeError) {
                setError(BUY_ERRORS[e.code] ?? e.message)
                if (e.code === 'price_changed') {
                    setStale(true)
                    onPriceChanged()
                }
            } else {
                setError('The purchase did not go through. You can try again; you will not be charged twice.')
            }
        } finally {
            setBusy(false)
        }
    }

    return (
        <TradeDialog open title={`Buy ${listing.nft.name}`} busy={busy} onClose={onClose}>
            <div className='flex flex-col gap-3'>
                <div className='flex flex-row items-center gap-3 border border-white/10 p-2'>
                    <div className='h-16 w-16 shrink-0 overflow-hidden bg-white/[0.03]'>
                        {listing.nft.image_url && (
                            <img
                                src={listing.nft.image_url}
                                alt={listing.nft.name}
                                className='h-full w-full object-contain'
                            />
                        )}
                    </div>
                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-bold text-white'>{listing.nft.name}</p>
                        <p className='truncate text-[10px] uppercase tracking-widest text-white/40'>
                            {listing.nft.operative_type} · sold by {listing.seller.username}
                        </p>
                    </div>
                    <div className='text-right'>
                        <p className='text-[10px] uppercase tracking-widest text-white/40'>Price</p>
                        <p className='text-lg font-black text-white'>${listing.price}</p>
                    </div>
                </div>

                {wallets.length === 0 ? (
                    <p className='text-xs text-[#FFC53D]'>
                        You need a linked wallet for us to deliver the NFT to.{' '}
                        <Link to='/account#wallets' className='underline'>
                            Link a wallet
                        </Link>{' '}
                        and come back.
                    </p>
                ) : (
                    <label className='flex flex-col gap-1 text-[11px] uppercase tracking-widest text-white/40'>
                        Deliver to
                        <select
                            value={wallet}
                            onChange={(e) => setWallet(e.target.value)}
                            disabled={busy}
                            className='w-full border border-white/15 bg-black px-3 py-2 text-sm font-bold normal-case tracking-normal text-white outline-none focus:border-[#03CEFF]'
                        >
                            {wallets.map((w) => (
                                <option key={w.pubkey} value={w.pubkey}>
                                    {w.name ? `${w.name} (${shortKey(w.pubkey)})` : shortKey(w.pubkey)}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <p className='text-xs text-white/40'>
                    ${listing.price} is taken from your BR1 balance now, and the NFT is sent to the wallet
                    above. No wallet signature is needed.
                </p>

                {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

                <button
                    onClick={confirm}
                    disabled={busy || stale || wallet === ''}
                    className='min-h-[2.75rem] bg-[#03CEFF] px-6 text-xs font-black uppercase tracking-[0.2em] text-black transition-[filter] hover:brightness-110 disabled:opacity-40 disabled:hover:brightness-100'
                >
                    {busy ? 'Buying...' : `Confirm and pay $${listing.price}`}
                </button>
            </div>
        </TradeDialog>
    )
}
